import { useContext, createContext, FC, useEffect, useReducer } from 'react';
import jwt_decode from 'jwt-decode';
import API from 'services/AxiosConfig';
import { userReducer, UserStateI, UserActionTypes, userInitialState } from 'store';
import { useCompany } from 'hooks';
import { isAdmin, resetFilters } from 'lib';
import { toast } from 'react-toastify';

const STATE_LS_KEY = 'user';

interface AuthInput {
  email: string;
  password: string;
}

type AuthFn<T = void> = ({ email, password }: AuthInput) => Promise<T>;

interface decoded {
  id: string;
  fullName: string;
  email: string;
  userRole: [string];
  location: string;
}

export interface Context extends UserStateI {
  signInWithEmail: AuthFn;
  logout: () => void;
}

const LSinitialState = JSON.parse(localStorage.getItem(STATE_LS_KEY) as string) || userInitialState;

export const AuthContext = createContext({} as Context);

export const AuthContextProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, LSinitialState);
  const { setLocations, setUsers, resetCompany, setClients, setCarePartners, setCareManagerActivities, setApplicants, setAwardTypes, setDeactivationReasons, setLostClientReasons, setApplicantStages, setReferralPartners, setCompanies, setProspect, setMilestones, setRatings, setCompanyRatings, setactivityTypes, setCompanyTypes, setApplicantsActivity, setLeadSources, setReferralActivityTypes } = useCompany();

  const logout: Context['logout'] = () => {
    dispatch({ type: UserActionTypes.LOGOUT });
    resetCompany();
    resetFilters();
    localStorage.removeItem('apexcare-token');
  };

  // const signInWithEmail: Context['signInWithEmail'] = async ({ email, password }) => {
  //   try {
  //     const response = await API.post('/auth/login', { email, password });
  //     localStorage.setItem('apexcare-token', response.data.token);
  //     const decoded: decoded = jwt_decode(response.data.token);
  //     const { firstName, lastName, _id, roles } = response.data.user;
  //     const userData = {
  //       _id,
  //       decodedId: decoded.id,
  //       firstName,
  //       lastName,
  //       email: decoded.email,
  //       location: response.data.user.location,
  //       roles
  //     };
  //     dispatch({ type: UserActionTypes.LOGIN, payload: userData });
  //     const locationFilter: any[] = []
  //     let locationsData: any = {};

  //     if (!isAdmin(roles)) {
  //       let locationPermissions: any = await API.get(`permissions/${_id}`)
  //       locationPermissions.data.data.map((permObj: any) => {
  //         locationFilter.push(permObj.location._id)
  //       })
  //       let queryString = locationFilter.join(',')
  //       if (queryString) {
  //         locationsData = await API.get(`location?locations=${queryString}`);
  //       } else {
  //         locationsData = { data: { data: [] } }
  //       }
  //     } else {
  //       locationsData = await API.get(`location/all?type=Active`)
  //     }
  //     setLocations(locationsData.data.data);
  //     const usersData = await API.get('user/Active');
  //     setUsers(usersData.data.data);
  //     const clientsData = await API.get('client');
  //     setClients(clientsData.data.data);
  //     const carePartnersData = await API.get('care-partner');
  //     setCarePartners(carePartnersData.data.data);
  //     const careManagerActivitiesData = await API.get('care-manager-activity');
  //     setCareManagerActivities(careManagerActivitiesData.data.data);

  //     const applicants = await API.get(`applicants/ddn/0`);
  //     setApplicants(applicants.data.data);
  //     if (applicants && applicants.data && applicants.data.data) {
  //       localStorage.setItem("applicants", JSON.stringify(applicants.data.data));
  //     }
  //     const awardTypesData = await API.get('awards/ddn/Active');
  //     if (awardTypesData && awardTypesData.data && awardTypesData.data.data) {
  //       setAwardTypes(awardTypesData.data.data);
  //     }

  //     const deactivationReasonData = await API.get('/client/activity/search/Active');
  //     if (deactivationReasonData && deactivationReasonData.data && deactivationReasonData.data.data) {
  //       setDeactivationReasons(deactivationReasonData.data.data);
  //     }
  //     const lostClientReasonsData = await API.get('/care-partner/activity/search/Active');
  //     if (lostClientReasonsData && lostClientReasonsData.data && lostClientReasonsData.data.data) {
  //       setLostClientReasons(lostClientReasonsData.data.data);
  //     }

  //     localStorage.setItem('openActivities', JSON.stringify([]))
  //   } catch (error: any) {
  //     toast.error(error?.response?.data.error || error)
  //   }
  // };
  const signInWithEmail: Context['signInWithEmail'] = async ({ email, password }) => {
    try {
      const response = await API.post('/auth/login', { email, password });
      localStorage.setItem('apexcare-token', response.data.token);

      const decoded: decoded = jwt_decode(response.data.token);
      const { firstName, lastName, _id, roles } = response.data.user;

      const userData = {
        _id,
        decodedId: decoded.id,
        firstName,
        lastName,
        email: decoded.email,
        location: response.data.user.location,
        roles,
        profileImage: response.data.user.profileImage,
      };

      dispatch({ type: UserActionTypes.LOGIN, payload: userData });

      // Load company data based on role
      let locationData;
      if (!isAdmin(roles)) {
        const perms = await API.get(`permissions/${_id}`);
        const locIds = perms.data.data.map((p: any) => p.location._id).join(',');
        locationData = locIds ? await API.get(`location?locations=${locIds}`) : { data: { data: [] } };
      } else {
        locationData = await API.get('location/all?type=Active');
      }
      setLocations(locationData.data.data);

      await Promise.all([
        API.get('user/Active').then(res => setUsers(res.data.data)),
        API.get('client/ddn/0').then(res => setClients(res.data.data)),
        API.get('care-partner/ddn/0').then(res => setCarePartners(res.data.data)),
        API.get('care-manager-activity').then(res => setCareManagerActivities(res.data.data)),
        API.get('applicants/ddn/0').then(res => setApplicants(res.data.data)),
        API.get('awards/ddn/Active').then(res => setAwardTypes(res.data.data)),
        API.get('/client/activity/search/Active').then(res => setDeactivationReasons(res.data.data)),
        API.get('/care-partner/activity/search/Active').then(res => setLostClientReasons(res.data.data)),
        API.get('applicants/stages').then(res => setApplicantStages(res.data.data)),
        API.get('referral-partners/ddn/0').then(res => setReferralPartners(res.data.data)),
        API.get('referral-partners/companies/ddn/0').then(res => setCompanies(res.data.data)),
        API.get('/prospects/ddn/all').then(res => setProspect(res.data.data)),
        API.get('/prospects/progress').then(res => setMilestones(res.data.data)),
        API.get('/rating-referral/activity/search/Active?referral=true').then(res => setRatings(res.data.data)),
        API.get('/rating-referral-company/activity/search/Active?referral=true').then(res => setCompanyRatings(res.data.data)),
        API.get(`marketing/types?referral=false`).then(res => setactivityTypes(res.data.data)),
        API.get(`referral-partners/types/listing`).then(res => setCompanyTypes(res.data.data)),
        API.get('applicants/activity').then(res => setApplicantsActivity(res.data.data)),
        API.get('/lead-source/activity/search/Active').then(res => setLeadSources(res.data.data)),
        API.get('marketing/types?referral=true').then(res => setReferralActivityTypes(res.data.data)),
        API.get('location/all?type=Active').then(res => setLocations(res.data.data)),

      ]);

      localStorage.setItem('openActivities', JSON.stringify([]));
    } catch (error: any) {
      toast.error(error?.response?.data?.error);
    }
  };

  useEffect(() => {
    localStorage.setItem(STATE_LS_KEY, JSON.stringify(state));
  }, [state]);

  return (
    <AuthContext.Provider
      value={{
        signInWithEmail,
        logout,
        ...state,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default function useAuth() {
  const context = useContext(AuthContext);

  return context;
}
